import React, { useRef, useState, useLayoutEffect, useCallback, useEffect } from "react";
import C from "classnames";

import { TabView, TabPanel } from "primereact/tabview";

// Other toolkit utils
// import { themeClasses as T } from "../../utils/themeClasses";

// Sub-components
import DrawerToggleButton from "./DrawerToggleBtn";
import DrawerContent from "./DrawerContent";

// Styles
import S from "./_styles.module.scss";
import { Badge } from "primereact/badge";

// Constants
const MinimumDrawerHeight = 52; // Minimum height in pixels when drawer is open
const DefaultInitialHeight = "25%"; // Default initial height when not specified

// Props interface
export interface DrawerProps {
  // From Parent of ResizableDrawer
  title?: string;
  className?: string;
  startOpen?: boolean;
  initialHeight?: string;
  overflow?: "auto" | "hidden" | "visible" | "scroll";
  children?: React.ReactNode;
  activeTab?: number;
  onTabChange?: (index: number) => void;
  isOpen?: boolean;
  onOpenToggle?: (isOpen: boolean) => void;
  badge?: number | string;
  onBadgeClear?: () => void;

  // From ResizableDrawer
  isResizing?: boolean;
  deltaHeight?: number | null;
}

// Main component
const Drawer: React.FC<DrawerProps> = ({
  // From Parent of PanelWithDrawers
  title,
  className,
  startOpen,
  initialHeight,
  overflow = "auto",
  children,
  activeTab,
  onTabChange,
  isOpen,
  onOpenToggle,

  // From PanelWithDrawers
  isResizing,
  deltaHeight,
}) => {
  // Figure out if we're rendering more than one child of DrawerContent type
  let countDrawerContentChildren = 0;
  React.Children.forEach(children, child => {
    if (React.isValidElement(child) && child.type === DrawerContent) {
      countDrawerContentChildren++;
    }
  });

  const useTabs = countDrawerContentChildren > 1;
  const ClosedHeight = useTabs ? `${MinimumDrawerHeight}px` : "auto";

  // Set up state
  const [isOpenInner, setIsOpenInner] = useState<boolean>(startOpen ?? initialHeight !== undefined);
  const [height, setHeight] = useState<string>(initialHeight ?? DefaultInitialHeight);
  const [activeTabInner, setActiveTabInner] = useState<number>(startOpen ? (activeTab ?? 0) : -1);
  const [badge, setBadge] = useState<string | number>(0);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Create refs that children can directly assign to
  // Note: This implementation currently only supports one child assigning to it
  const onBadgeClearRef = useRef<(() => void) | null>(null);

  // Compute effective isOpen value (controlled vs uncontrolled)
  const isOpenValue = isOpen !== undefined ? isOpen : isOpenInner;

  // Handle controlled isOpen prop - sync when it changes
  useEffect(() => {
    if (isOpen !== undefined) {
      setIsOpenInner(isOpen);
    }
  }, [isOpen]);

  // Handle controlled activeTab prop - sync when it changes
  useEffect(() => {
    if (activeTab !== undefined) {
      setActiveTabInner(activeTab);
    }
  }, [activeTab]);

  // Propagate isOpen change events
  const setIsOpen = (newIsOpen: boolean) => {
    // In controlled mode, isOpen prop change will update via effect
    if (onOpenToggle) {
      onOpenToggle(newIsOpen);
    }
    if (isOpen === undefined) {
      // Uncontrolled mode
      setIsOpenInner(newIsOpen);
    }
  };

  // Propagate tab change events
  const setActiveTab = (newIndex: number) => {
    // In controlled mode, activeTab prop change will update via effect
    if (onTabChange) {
      onTabChange(newIndex);
    }
    if (activeTab === undefined) {
      // Uncontrolled mode
      setActiveTabInner(newIndex);
    }
  };

  // Method to synchronize drawer height state with actual height
  const syncCurrentHeight = useCallback(() => {
    if (!drawerRef.current) return;

    const currentDrawerHeight = `${getCurrentHeight()}px`;
    if (height !== currentDrawerHeight) {
      setHeight(currentDrawerHeight);
    }
  }, [height]);

  // Method to get the current height of the drawer
  const getCurrentHeight = () => {
    if (!drawerRef.current) return 0;
    return drawerRef.current.offsetHeight;
  };

  // Toggle drawer height based on isOpen state
  useLayoutEffect(() => {
    if (!drawerRef.current) return;

    if (isOpenValue && drawerRef.current.style.height !== height) {
      drawerRef.current.style.height = height;
      drawerRef.current.style.minHeight = height;
    } else {
      drawerRef.current.style.height = ClosedHeight;
      drawerRef.current.style.minHeight = ClosedHeight;
    }
  }, [isOpenValue, height, ClosedHeight]);

  // If isResizing changes, ensure the drawer is in open state
  useLayoutEffect(() => {
    // If parent sets isResizing to true and the drawer isn't open,
    // sync its height and open it so the user can start resizing it
    if (isResizing) {
      if (!isOpenValue) {
        syncCurrentHeight();
        setIsOpen(true);
      }
    }
    // If parent sets isResizing to false and the drawer is open,
    // check to see if we are at 0 height. If so, set isOpen to false
    // and reset height state to initialHeight.
    else {
      if (isOpenValue && getCurrentHeight() <= MinimumDrawerHeight) {
        setIsOpen(false);
        // Reset height to initialHeight or default
        setHeight(initialHeight ?? DefaultInitialHeight);
      }
    }
  }, [isResizing, isOpenValue, syncCurrentHeight, initialHeight]);

  // Calculate a specific height for the drawer when deltaHeight changes
  useLayoutEffect(() => {
    if (!drawerRef.current) return;

    // If deltaHeight is provided, calculate new height (minimum of MinimumDrawerHeight)
    if (deltaHeight !== null && deltaHeight !== undefined) {
      const newHeight = Math.max(getCurrentHeight() + deltaHeight, MinimumDrawerHeight);
      const newHeightPx = `${newHeight}px`;
      setHeight(newHeightPx);
    }
  }, [deltaHeight]);

  // Handle toggle button click
  const handleToggleClick = () => {
    const newIsOpen = !isOpenValue;
    setIsOpen(newIsOpen);

    if (onBadgeClearRef.current) {
      onBadgeClearRef.current();
    }
  };

  // Handle tab click by (a) opening the drawer and (b) setting active tab
  const handleTabClick = (e: { index: number }) => {
    setIsOpen(true);
    setActiveTab(e.index);
  };

  // Method to open the drawer (used by children)
  const openDrawer = () => {
    if (!isOpenValue) {
      setIsOpen(true);
    }
  };

  // Method to update badge value (used by children)
  const updateBadge = useCallback((value: string | number) => {
    setBadge(value);
  }, []);

  return (
    <div
      className={C("p-splitter-panel flex-col pb-3", S.drawer, className, {
        ["mt-2"]: useTabs,
        [S.drawerClosed]: !isOpenValue,
      })}
      role="presentation"
      ref={drawerRef}
    >
      {!useTabs && (
        <>
          <div
            className={C("flex items-center", S.drawerHeader)}
            id="resizable-drawer-bottom-panel-header"
          >
            {title && (
              <div className="body-xs uppercase font-bold flex-1 pl-4 flex items-center gap-2 min-h-5">
                <span>{title}</span>
                {!isOpenValue && !!badge && <Badge value={badge} />}
              </div>
            )}
            <DrawerToggleButton
              isPanelOpen={isOpenValue}
              onToggleClick={handleToggleClick}
              title={title}
            />
          </div>
          <div className={C(S.drawerContents, { hidden: !isOpenValue })}>
            <div className={C(S.frameContainer)}>
              {React.Children.map(children, child => {
                if (React.isValidElement(child)) {
                  return React.cloneElement(child, {
                    openDrawer,
                    isOpenValue,
                    onBadgeClearRef,
                    updateBadge,
                  });
                }
                return child;
              })}
              <div className={C(S.resizingOverlay, { [S.visible]: isResizing })}></div>
            </div>
          </div>
        </>
      )}
      {useTabs && (
        <div className={C(S.drawerContents)}>
          <DrawerToggleButton
            isPanelOpen={isOpenValue}
            onToggleClick={handleToggleClick}
            floating={true}
            title={title}
          />
          <TabView
            aria-label="Drawer tabs"
            className={C("h-full", S.frameContainer)}
            activeIndex={isOpenValue ? activeTabInner : -1}
            renderActiveOnly={true}
            onTabChange={handleTabClick}
            pt={{
              panelContainer: {
                className: C(`p-0 h-full overflow-y-${overflow}`, {
                  ["hidden"]: !isOpenValue,
                }),
              },
            }}
          >
            {React.Children.map(children, (child, index) => {
              if (React.isValidElement(child) && child.type === DrawerContent) {
                const tabTitle = child.props.title || `Tab ${index + 1}`;
                return (
                  <TabPanel key={index} header={tabTitle}>
                    {React.cloneElement(child, { openDrawer })}
                  </TabPanel>
                );
              }
              return null;
            })}
          </TabView>
          <div className={C(S.resizingOverlay, { [S.visible]: isResizing })}></div>
        </div>
      )}
    </div>
  );
};

export default Drawer;
